import React from 'react';
import { useWatch } from 'react-hook-form';
import { Box, Button, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useAppDispatch } from '../../../../../../../app/hooks';
import { pushMessage } from '../../../../../../../features/global-messages/global-messages-slice';
import { copyToClipboard } from '../../../../../../../helpers';
import {
    FROM_ACCOUNT,
    ITransfer,
    TO_ACCOUNT,
    TRANSFER_RECEIPTS,
} from '../transfer-form';
import { receiptFolio } from '../receipt-folio';

const AMOUNT: keyof ITransfer = 'amount';

// "Utilidades" tab: quick copy-to-clipboard helpers built from the transfer's
// live values (account + the receipts' document folios + the total).
export function TransferUtilities() {
    const dispatch = useAppDispatch();

    const fromAccount = useWatch({
        name: FROM_ACCOUNT,
    }) as ITransfer['from_account'];
    const toAccount = useWatch({ name: TO_ACCOUNT }) as ITransfer['to_account'];
    const receipts = useWatch({
        name: TRANSFER_RECEIPTS,
    }) as ITransfer['transfer_receipts'];
    const amount = useWatch({ name: AMOUNT }) as ITransfer['amount'];

    // One folio per receipt (expense external code, or sale invoice/order code).
    const codes = (receipts ?? []).map((r) => receiptFolio(r)).filter(Boolean);

    // Counterparty (non-"own") account name, slugified: lowercased, commas
    // dropped (business names like "Empresa, S.A."), spaces replaced by hyphens.
    const rawAccountName =
        [fromAccount, toAccount].find((a) => a?.name && !a.is_own)?.name ??
        fromAccount?.name ??
        toAccount?.name ??
        '';
    const accountSlug = rawAccountName
        .toLowerCase()
        .replace(/,/g, '')
        .replace(/\s+/g, '-');

    const items = [
        {
            label: 'Cuenta + folios (separados por "-")',
            value: [accountSlug, ...codes].filter(Boolean).join('-'),
        },
        {
            label: 'Folios (separados por coma)',
            value: codes.join(', '),
        },
        {
            label: 'Total de la transferencia',
            value: String(amount ?? 0),
        },
    ];

    const handleCopy = async (value: string) => {
        const ok = await copyToClipboard(value);
        dispatch(
            pushMessage({
                message: ok ? 'Copiado al portapapeles' : 'No se pudo copiar',
                options: { variant: ok ? 'success' : 'error' },
            }),
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {items.map((item) => (
                <Box
                    key={item.label}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant={'caption'} color={'text.secondary'}>
                            {item.label}
                        </Typography>
                        <Typography
                            variant={'body2'}
                            sx={{ wordBreak: 'break-all' }}
                        >
                            {item.value || '—'}
                        </Typography>
                    </Box>
                    <Button
                        variant={'outlined'}
                        size={'small'}
                        startIcon={<ContentCopy />}
                        disabled={!item.value}
                        onClick={() => void handleCopy(item.value)}
                    >
                        Copiar
                    </Button>
                </Box>
            ))}
        </Box>
    );
}
